import { kebabCase } from 'lodash/fp';
import { useMemo, VFC } from 'react';

import Accordion from 'dashboard/components/Accordion';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { getTokensToDisplayValue, UsableObjectConfig } from 'dashboard/lib/workflows';
import { ListStatus } from 'dashboard/models/list';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionAddItemToListV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

type SupportedListType =
  | ApiModel.ListBrowserFingerprint
  | ApiModel.ListDeviceFingerprint
  | ApiModel.ListCountry
  | ApiModel.ListEmailAddress
  | ApiModel.ListField
  | ApiModel.ListGovernmentIdNumber
  | ApiModel.ListIpAddress
  | ApiModel.ListName
  | ApiModel.ListPhoneNumber
  | ApiModel.ListSelfieBackground
  | ApiModel.ListString;

const ListItemFields: Record<SupportedListType, string[]> = {
  [ApiModel.ListBrowserFingerprint]: ['browser-fingerprint'],
  [ApiModel.ListDeviceFingerprint]: ['device-fingerprint'],
  [ApiModel.ListCountry]: ['country-code'],
  [ApiModel.ListEmailAddress]: ['email-address'],
  [ApiModel.ListField]: ['field'],
  [ApiModel.ListGovernmentIdNumber]: ['id-class', 'id-number'],
  [ApiModel.ListIpAddress]: ['ip-address'],
  [ApiModel.ListName]: ['name-first', 'name-last'],
  [ApiModel.ListPhoneNumber]: ['phone-number'],
  [ApiModel.ListSelfieBackground]: ['selfie-background'],
  [ApiModel.ListString]: ['string'],
};

interface ParamsAddItemToListV1Props extends ParamsBaseProps {
  lists: WorkflowActionMeta['lists'];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionAddItemToListV1['params'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionAddItemToListV1['params']) => void;
}

const ParamsAddItemToListV1: VFC<ParamsAddItemToListV1Props> = ({
  lists,
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
  updateStep,
  step,
}) => {
  const hasListPerm = usePermission(Permission.ListView);
  const selfieBackgroundListEnabled = useFeatureFlag('dashboard:selfie-background-list');
  const isTemplateAvailable = useMemo(() => {
    return !params['list-id'] || lists.map((list) => list.id).includes(params['list-id']);
  }, [lists, params]);
  const availableLists = useMemo(() => {
    return (
      lists
        ?.filter(
          (list) =>
            list.attributes['status'] === ListStatus.Active &&
            ListItemFields[list.type as SupportedListType] &&
            //Only show selfie background list if feature flag is enabled
            (list.type === ApiModel.ListSelfieBackground ? selfieBackgroundListEnabled : true),
        )
        .map((list) => ({
          label: list.name,
          value: list.id,
        })) || []
    );
  }, [lists, selfieBackgroundListEnabled]);

  const updateList = (listId: WorkflowActionAddItemToListV1['params']['list-id']) => {
    const listName = availableLists.find((l) => l.value === listId)?.label;
    updateStep({
      ...step,
      name: `Add to List: ${listName}`,
      action: {
        ...(step.action as WorkflowActionAddItemToListV1),
        params: {
          ...params,
          'list-id': listId,
          'list-item-fields': {},
        },
      },
    });
  };

  const updateListItemFields = (value: Record<string, string>) => {
    updateActionParams({
      ...params,
      'list-item-fields': Object.keys(value).reduce<Record<string, string>>(
        (acc, elem) => ({
          ...acc,
          [elem]: value[elem],
        }),
        {},
      ),
    });
  };

  const [
    showFieldMappingsPanel,
    { toggle: toggleFieldMappingsPanel, disable: closeFieldMappingsPanel },
  ] = useToggle(false);

  const fieldOptions = (
    ListItemFields[lists.find((l) => l.id === params['list-id'])?.type as SupportedListType] || []
  ).reduce<Record<string, string>>(
    (acc, field) => ({
      ...acc,
      [kebabCase(field)]: field,
    }),
    {},
  );

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionAddItemToListV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='List'
        htmlFor='params-add-item-to-list__list-input'
        helpText={
          hasListPerm
            ? isTemplateAvailable
              ? undefined
              : 'The current list is not available to you at this time.'
            : 'Your role does not have permissions to view lists.'
        }
      />
      <ReactSelect
        value={params['list-id']}
        options={availableLists}
        onChange={updateList}
        disabled={!editable || !hasListPerm}
        placeholder={hasListPerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'}
        hasError={hasErrorFor('list-id')}
        id='params-add-item-to-list__list'
      />
      {renderErrorMessageFor('list-id')}
      <Spacer height={2} />
      {isTemplateAvailable && (
        <Accordion title='Item Fields' defaultExpanded>
          <Spacer height={2} />

          {showFieldMappingsPanel && (
            <FieldMappingsPanel
              title='List Item Fields'
              usableObjects={usableObjects}
              fieldMappings={params['list-item-fields'] ?? {}}
              presetFields={fieldOptions}
              onOutsideClick={closeFieldMappingsPanel}
              onUpdateFieldMappings={updateListItemFields}
            />
          )}
          <FieldsList
            fields={params['list-item-fields']}
            fieldKey='list-item-fields'
            toggleEditPanel={toggleFieldMappingsPanel}
            tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
            editable={editable && Object.keys(fieldOptions).length > 0}
            placeholderEmptyText={
              Object.keys(fieldOptions).length === 0
                ? 'List has no fields configuration'
                : 'No fields selected'
            }
            errors={errors}
          />
          {renderErrorMessageFor('list-item-fields')}
        </Accordion>
      )}
    </>
  );
};

export default ParamsAddItemToListV1;
